import { inject, Injectable } from '@angular/core';
import { SessionStorageService } from '../../../core/services/session-storage/session-storage.service';
import { AD_ACCEPTED_FORMATS, AD_BRANCHES, AD_FILE_KIND_META, AdAttachment, AdFileKind, AdStatus, MARKETING_REVIEWER_ROLES, MarketingAd, PENDING_REVIEW_STATUSES } from '../marketing-review-interfaces/marketing-review.interface';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { environment } from '../../../../environments/environment';

const SAMPLES: string = 'assets/marketing-review-samples/';

/** File family per extension — the primary signal behind `fileKindOf()`. */
const KIND_BY_EXTENSION: Record<string, AdFileKind> = {
    png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image', bmp: 'image', svg: 'image',
    mp4: 'video', mov: 'video', webm: 'video', m4v: 'video',
    pdf: 'pdf',
    ppt: 'slides', pptx: 'slides',
    doc: 'document', docx: 'document', txt: 'document',
    xls: 'spreadsheet', xlsx: 'spreadsheet', csv: 'spreadsheet',
};

/** Badge text per extension, so `.pptx` reads `PPT` rather than its MIME subtype. */
const BADGE_BY_EXTENSION: Record<string, string> = {
    png: 'PNG', jpg: 'JPG', jpeg: 'JPG', gif: 'GIF', webp: 'WEBP', bmp: 'BMP', svg: 'SVG',
    mp4: 'MP4', mov: 'MOV', webm: 'WEBM', m4v: 'MP4',
    pdf: 'PDF',
    ppt: 'PPT', pptx: 'PPT',
    doc: 'DOC', docx: 'DOC', txt: 'TXT',
    xls: 'XLS', xlsx: 'XLS', csv: 'CSV',
};

/**
 * Marketing Review data access.
 *
 * ⚠️ The backend for this module does not exist yet, so every method resolves from an in-memory
 * store seeded with `MOCK_ADS`. The store lives in a `BehaviorSubject` so a create or a review on
 * one page is reflected on the others for the rest of the session (it resets on reload).
 *
 * Each method carries the axios block it will become, matching the shape used across the app
 * (see `pages/campaign/campaign-service/campaign-service.ts`). Swapping one over means deleting
 * the mock body, uncommenting the request, and adding its endpoint key to all four
 * `src/environments/environment*.ts` files (`''` in `environment.ts`).
 */
@Injectable({
    providedIn: 'root'
})
export class MarketingReviewService {

    private sessionStorageService = inject(SessionStorageService);
     private readonly MOCK_LATENCY_MS: number = 300;

    constructor() { }


    async getAdById(applicationId: number) {
        const API_URL: string = environment.baseUrl + environment.apis.actionbyMarketingReview + `/${applicationId}`;
        const REQUEST_CONFIG: AxiosRequestConfig = {
            method: 'GET',
            maxBodyLength: Infinity,
            url: API_URL,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
            },
        };

        return await axios.request(REQUEST_CONFIG)
            .then((httpResponse) => {
                return JSON.stringify(httpResponse);
            })
            .catch((error) => {
                throw new AxiosError(error.response.data, error.status);
            });
    }

    /**
     * Whether the signed-in user is on the marketing team, which decides the dashboard view.
     * See `MARKETING_REVIEWER_ROLES` — currently a placeholder, so this is `false` for everyone.
     */
    isMarketingReviewer(): boolean {
        return MARKETING_REVIEWER_ROLES.includes(this.sessionStorageService.getCurrentUserRole());
    }

    /**
     * True when the signed-in user raised the ad. Matched on rep code, falling back to the display
     * name for records raised without one.
     */
    private isOwnedByCurrentUser(ad: MarketingAd): boolean {
        const repCode: string = String(this.sessionStorageService.getCurrentRepCode() ?? '');
        // Same fallback the seed uses for `createdBy`, so the dashboard is not empty in a session
        // without stored user details.
        const userName: string = this.sessionStorageService.getCurrentUserPersonName() || 'Current User';

        return repCode ? ad.createdByRepCode === repCode : ad.createdBy === userName;
    }

    /**
     * Branches selectable on the application form.
     *
     * TODO(backend): serve this from `environment.apis.getBranchName`
     * (`/v2/pias/agentmaintenance/getbranch`) with the Authorization header, as Agent Maintenance
     * already does, instead of the `AD_BRANCHES` mock.
     */
    async listBranches(): Promise<string[]> {
        return [...AD_BRANCHES];
    }



    /** Days an ad has been waiting since submission; `0` when it was never submitted. */
    daysWaiting(ad: MarketingAd): number {
        if (!ad.submittedOn) {
            return 0;
        }
        const elapsedMs: number = Date.now() - new Date(ad.submittedOn).getTime();
        return Math.max(0, Math.floor(elapsedMs / (1000 * 60 * 60 * 24)));
    }

    /** Two-letter avatar initials, e.g. `Galvin Lim` → `GL`. */
    private initialsOf(name: string): string {
        const parts: string[] = name.trim().split(/\s+/).filter(Boolean);
        if (!parts.length) {
            return '–';
        }
        const first: string = parts[0][0] ?? '';
        const last: string = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
        return (first + last).toUpperCase();
    }
     async uploadAdAsset(file: File): Promise<AdAttachment> {
        await this.simulateLatency();

        return {
            id: Date.now(),
            fileName: file.name,
            fileType: file.type || 'application/octet-stream',
            fileSizeKb: Math.max(1, Math.round(file.size / 1024)),
            url: URL.createObjectURL(file),
        };
    }


    /**
     * Upload ceilings. Video gets its own — a 30-second banner cut is routinely past the limit
     * that is generous for a deck or a flyer.
     */
    readonly MAX_FILE_SIZE_MB: number = 10;
    readonly MAX_VIDEO_SIZE_MB: number = 50;

    /**
     * Sorts picked or dropped files into what may be uploaded and what may not, by type first and
     * then by size. Shared by the create page and the review page's re-upload, so both enforce the
     * same rules. `accept` on the input only filters the picker; drag-and-drop bypasses it.
     */
    screenAdFiles(files: File[]): { accepted: File[]; wrongType: File[]; tooLarge: File[] } {
        const extensions: string[] = AD_ACCEPTED_FORMATS.flatMap((format) => format.extensions);
        const isAcceptedType = (file: File): boolean => (
            extensions.some((extension) => file.name.toLowerCase().endsWith(extension))
        );
        const isTooLarge = (file: File): boolean => {
            const isVideo: boolean = this.fileKindOf(file.name) === 'video';
            return file.size > (isVideo ? this.MAX_VIDEO_SIZE_MB : this.MAX_FILE_SIZE_MB) * 1024 * 1024;
        };

        const rightType: File[] = files.filter(isAcceptedType);

        return {
            wrongType: files.filter((file) => !isAcceptedType(file)),
            tooLarge: rightType.filter(isTooLarge),
            accepted: rightType.filter((file) => !isTooLarge(file)),
        };
    }

    // ── Helpers ─────────────────────────────────────────────────────────────────

    /** True when the status still needs a reviewer to act on it. */
    isPendingReview(status: AdStatus): boolean {
        return PENDING_REVIEW_STATUSES.includes(status);
    }

    /** Every branch named across the given ads, deduplicated and sorted. */
    private branchNamesOf(ads: MarketingAd[]): string[] {
        return [...new Set(ads.flatMap((ad) => ad.branches))].filter(Boolean).sort();
    }

    // ── File kinds ──────────────────────────────────────────────────────────────

    /**
     * File family of an attachment, resolved from its extension first and its MIME type second.
     *
     * The extension wins because the Office MIME types are both long and easy to lose: a `.pptx`
     * dragged in from some clients arrives as `application/octet-stream`, which would otherwise
     * fall through to `other` and lose its document card.
     */
    fileKindOf(attachment: string): AdFileKind {
        const byExtension: AdFileKind | undefined = KIND_BY_EXTENSION[this.extensionOf(attachment)];
        if (byExtension) {
            return byExtension;
        }

        const fileType: string = (attachment).toLowerCase();
        if (fileType.startsWith('image/')) {
            return 'image';
        }
        if (fileType.startsWith('video/')) {
            return 'video';
        }
        if (fileType === 'application/pdf') {
            return 'pdf';
        }
        if (fileType.includes('presentation') || fileType.includes('powerpoint')) {
            return 'slides';
        }
        if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType === 'text/csv') {
            return 'spreadsheet';
        }
        if (fileType.includes('word') || fileType === 'application/msword' || fileType === 'text/plain') {
            return 'document';
        }
        return 'other';
    }

    /** Short badge for an attachment, e.g. `PNG`, `MP4`, `PPT`. */
    formatLabelOf(attachment: string): string {
        return BADGE_BY_EXTENSION[this.extensionOf(attachment)]
            ?? AD_FILE_KIND_META[this.fileKindOf(attachment)].label;
    }

    /** Material icon ligature for an attachment's kind. */
    iconOf(attachment: string): string {
        return AD_FILE_KIND_META[this.fileKindOf(attachment)].icon;
    }

    /** Palette key for an attachment's kind, used to tint its document card and badge. */
    toneOf(attachment: string): string {
        return AD_FILE_KIND_META[this.fileKindOf(attachment)].tone;
    }

    /** `1.2 MB` / `412 KB`, from the stored kilobyte size. */
    fileSizeLabel(fileSizeKb: number): string {
        return fileSizeKb >= 1024
            ? `${(fileSizeKb / 1024).toFixed(1)} MB`
            : `${fileSizeKb} KB`;
    }

    /** Lowercase extension without the dot, `''` when the name carries none. */
    private extensionOf(fileName: string): string {
        const name: string = (fileName || '').trim().toLowerCase();
        const dot: number = name.lastIndexOf('.');
        return dot > -1 ? name.slice(dot + 1) : '';
    }
     private simulateLatency(): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, this.MOCK_LATENCY_MS));
    }

    //GET Marketing Application List

    async getAllMarketingReview(repcode: any) {

        const API_URL: string = environment.baseUrl + environment.apis.getAllMarketingReview;

        const REQUEST_CONFIG: AxiosRequestConfig = {
            method: 'POST',
            maxBodyLength: Infinity,
            url: API_URL,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
            },
            data: {
                "piasRepCode": repcode
            }
        };

        return await axios.request(REQUEST_CONFIG)
            .then((httpResponse) => {
                return JSON.stringify(httpResponse);
            })
            .catch((error) => {
                throw new AxiosError(error.response.data, error.status);
            });
    }

    async deleteMarketingReviewApplication(data: any) {
        const API_URL: string =
            environment.baseUrl + environment.apis.deletedraft;

        const REQUEST_CONFIG: AxiosRequestConfig = {
            method: 'DELETE',
            maxBodyLength: Infinity,
            url: API_URL,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
            },
            data: {
                applicationId: data.applicationId,
                piasRepCode: data.piasRepCode
            }
        };

        return await axios.request(REQUEST_CONFIG)
            .then((httpResponse) => JSON.stringify(httpResponse))
            .catch((error) => {
                throw new AxiosError(
                    error.response?.data,
                    error.response?.status
                );
            });
    }

    async gethistory(marketingReviewApplicationId: number) {
        const API_URL: string = environment.baseUrl + environment.apis.history.
            replace('{applicationId}', String(marketingReviewApplicationId));
        const REQUEST_CONFIG: AxiosRequestConfig = {
            method: 'GET',
            maxBodyLength: Infinity,
            url: API_URL,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
            },
        };

        return await axios.request(REQUEST_CONFIG)
            .then((httpResponse) => {
                return httpResponse.data;
            })
            .catch((error) => {
                throw new AxiosError(error.response?.data, error.status);
            });
    }

}
